import React, { useEffect, useRef, useState } from "react";
import { ClickAwayListener, Popper } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { normalizeWizardTimeInput } from "./wizardShared";

const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const eventTimeLabel = (value: string): string => {
  const normalized = normalizeWizardTimeInput(value);
  if (!timePattern.test(normalized)) return value;
  const [hour, minute] = normalized.split(":").map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
};

type EventTimeInputProps = {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  // Step 2 passes "Select Start Time" / "Select End Time" (Create Event
  // Screen.dc.html:666, 671); agenda rows and other callers keep the default.
  placeholder?: string;
};

export default function EventTimeInput({ ariaLabel, value, onChange, error, placeholder = "Select Time" }: EventTimeInputProps): React.ReactElement {
  const anchor = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(eventTimeLabel(value));
  const [picked, setPicked] = useState("18:00");
  useEffect(() => { setDraft(eventTimeLabel(value)); }, [value]);
  const show = () => {
    const normalized = normalizeWizardTimeInput(value);
    setPicked(timePattern.test(normalized) ? normalized : "18:00");
    setOpen(true);
  };
  const apply = (hour: number, minute: number) => {
    const next = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    setPicked(next);
    setDraft(eventTimeLabel(next));
    onChange(next);
  };
  const [hour, minute] = picked.split(":").map(Number);
  const stepHour = (delta: number) => apply(((hour % 12 + delta + 12) % 12) + (hour >= 12 ? 12 : 0), minute);
  const stepMinute = (delta: number) => apply(hour, ((Math.round(minute / 15) + delta + 4) % 4) * 15);
  const commit = () => {
    const next = normalizeWizardTimeInput(draft);
    if (!draft.trim()) { onChange(""); return; }
    if (timePattern.test(next)) {
      setDraft(eventTimeLabel(next));
      if (next !== value) onChange(next);
    } else setDraft(eventTimeLabel(value));
  };
  const spinner = (label: string, displayed: string, change: (delta: number) => void) => (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{label}</span>
      <button type="button" aria-label={`Previous ${label.toLowerCase()}`} className="h-[22px] w-12 rounded hover:bg-gray-100" onMouseDown={(e) => e.preventDefault()} onClick={() => change(-1)}><KeyboardArrowUpIcon fontSize="small" /></button>
      <span className="min-w-10 text-center text-xl font-bold leading-[30px] tabular-nums">{displayed}</span>
      <button type="button" aria-label={`Next ${label.toLowerCase()}`} className="h-[22px] w-12 rounded hover:bg-gray-100" onMouseDown={(e) => e.preventDefault()} onClick={() => change(1)}><KeyboardArrowDownIcon fontSize="small" /></button>
    </div>
  );
  // Claude Design (8) ds-components/TimeField.jsx:89: #9E9E9E border, #F0202E on error.
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div ref={anchor} className={`flex h-[42px] w-full items-center gap-2 rounded-lg border bg-white px-3 ${error ? "border-[#F0202E]" : "border-[#9E9E9E]"}`}>
        <AccessTimeIcon className="text-gray-500" sx={{ fontSize: 16 }} />
        <input aria-label={ariaLabel} aria-invalid={error || undefined} aria-haspopup="dialog" value={draft} placeholder={placeholder} maxLength={10}
          className="min-w-0 flex-1 bg-transparent text-[14px] outline-none" onFocus={show} onClick={show}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); setOpen(false); }
            if (e.key === "Escape") { setDraft(eventTimeLabel(value)); setOpen(false); }
            if (open && (e.key === "ArrowUp" || e.key === "ArrowDown")) { e.preventDefault(); stepHour(e.key === "ArrowUp" ? 1 : -1); }
          }} />
        <button type="button" aria-label={`Choose ${ariaLabel.toLowerCase()}`} aria-expanded={open} aria-haspopup="dialog" onClick={show}
          onKeyDown={(event) => { if (event.key === "Escape") { setOpen(false); event.stopPropagation(); } }}
          className="rounded text-gray-500 focus:outline focus:outline-2"><KeyboardArrowDownIcon sx={{ fontSize: 16 }} /></button>
        <Popper open={open} anchorEl={anchor.current} placement="bottom-start" sx={{ zIndex: 1500 }} modifiers={[{ name: "offset", options: { offset: [0, 6] } }, { name: "preventOverflow", options: { padding: 8 } }]}>
          <div role="dialog" aria-label={`${ariaLabel} picker`} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }} className="flex w-[232px] max-w-[calc(100vw-16px)] items-center justify-center gap-2.5 rounded-[10px] border border-gray-100 bg-white px-3 pb-3 pt-2.5 text-gray-950 shadow-lg">
            {spinner("Hour", String(hour % 12 || 12), stepHour)}
            <span className="pt-3.5 text-xl font-bold text-gray-500">:</span>
            {spinner("Min", String(minute).padStart(2, "0"), stepMinute)}
            <div className="ml-1 flex flex-col gap-1.5 pt-3.5">{["AM", "PM"].map((period) => {
              const selected = (hour >= 12 ? "PM" : "AM") === period;
              return <button type="button" key={period} aria-pressed={selected} onMouseDown={(e) => e.preventDefault()} onClick={() => apply(hour % 12 + (period === "PM" ? 12 : 0), minute)} className={`w-[46px] rounded-md py-1.5 text-[13px] ${selected ? "bg-cpaGold font-bold text-white" : "bg-gray-100"}`}>{period}</button>;
            })}</div>
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
